import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concat, of } from 'rxjs';
import { catchError, map, take, timeout, toArray } from 'rxjs/operators';
import { environment } from 'environments/environment';

/**
 * StorageService
 *
 * Client for the Microservices API S3 presign endpoints. Angular/RxJS port of
 * cs-legacy's `app/core/services/storage.service.js` (branch
 * feature/s3-presign-frontend), against the same endpoint contract.
 *
 * Background: uploaded images used to live on the EC2 volume and were served as
 *   <host>/api/public/uploads/<company_id>/<table>/<record_id>/<filename>
 * They have since been migrated to S3, so those URLs now 404 — which is why the
 * Proof thumbnail on the orders table renders broken. The backend still builds
 * that path verbatim (see api-microservices
 * `apps/legacy/src/legacy.service.ts` → `formatPlatformOrdersImages`, which
 * emits `mockup_img` as
 * `http://dp.culturestudio.net/api/public/uploads/{company_id}/order_design_position/{position_id}/{image_1}`).
 *
 * This service asks the gateway to presign the equivalent S3 object and swaps
 * the URL in where S3 has it.
 *
 * CONTRACT — every resolver fails soft. On any error (gateway down, bad payload,
 * timeout) it logs a warning and leaves the backend's original URLs intact. A
 * gateway outage must degrade to today's behaviour, never to a broken page or a
 * thrown exception. "S3 if present, existing behaviour otherwise" is the core
 * guarantee.
 *
 * Rows are mutated IN PLACE, and resolvers are awaited *before* the list
 * observable emits. This differs from cs-legacy, which mutated fire-and-forget
 * and let the next digest pick it up: here the NgRx selector (`mapOrderListItem`)
 * copies rows into fresh view-model objects, so a mutation landing after the
 * emission would never reach the template.
 *
 * NOTE: every request is capped with `take(1)`. AuthInterceptor wraps requests
 * in Auth0's never-completing `user$`, so without it the sequential chunk chain
 * (and any forkJoin/toArray) would hang forever. Same reason the resolvers carry
 * a timeout — they gate the orders table's first paint.
 */

/** One entry in a presign-batch request. */
export interface PresignFileRequest {
  /** S3 key, `{companyId}/{table}/{recordId}/{filename}`. */
  path: string;
  /** Also presign the `/thumb/` variant of the same key. */
  includeThumb?: boolean;
}

/** One entry in a presign-batch response. Index-aligned with the request. */
export interface PresignFileResult {
  /** False when S3 has no such object — caller must keep the original URL. */
  exists: boolean;
  /** Presigned URL for the original. */
  url?: string;
  /** Presigned URL for the thumbnail variant, when `includeThumb` was set. */
  thumbUrl?: string;
}

export interface PresignBatchResponse {
  success: boolean;
  files: PresignFileResult[];
}

/**
 * One image to resolve: the S3 key to presign, plus how to write the result back
 * onto whatever object owns it. Lets the resolvers below share the batching,
 * index-alignment and fail-soft logic while differing only in field names.
 */
interface ResolveTarget {
  key: string;
  apply: (result: PresignFileResult) => void;
}

@Injectable({ providedIn: 'root' })
export class StorageService {

  /**
   * The presign endpoint fans out one or two HeadObject calls per file with no
   * server-side cap, so the client keeps batches modest and chunks larger sets.
   */
  private static readonly MAX_BATCH = 150;
  private static readonly DEFAULT_EXPIRES_IN = 3600;

  /** Give up rather than hold up the first paint of a list view. */
  private static readonly RESOLVE_TIMEOUT_MS = 8000;

  /** Legacy uploads URLs are `.../uploads/{companyId}/{table}/{recordId}/{filename}`. */
  private static readonly UPLOADS_MARKER = '/uploads/';

  /**
   * Base path of the presign endpoints on the gateway, taken from cs-legacy's
   * working implementation.
   *
   * UNVERIFIED FROM THIS REPO: the api-microservices gateway mounts its AWS
   * routes at `@Controller('aws')` and versions elsewhere as `api/v1/...`, so
   * this `v1/aws/storage` prefix does not match anything on the api-microservices
   * branches available locally — the presign backend lives on a branch that
   * isn't checked out here. The gateway also 401s every path (auth runs before
   * routing), so a probe can't distinguish a real route from a missing one.
   * If the deployed prefix turns out to differ, this constant is the only edit
   * needed. Everything downstream fails soft in the meantime, so a wrong prefix
   * degrades to today's behaviour rather than breaking the page.
   */
  private static readonly STORAGE_BASE = 'v1/aws/storage';

  constructor(private http: HttpClient) {}

  // =====================================================================
  // Low level transport
  // =====================================================================

  /**
   * Presign a single file by its component parts.
   *
   * `GET v1/aws/storage/uploads/:companyId/:type/:typeId/:filename`
   */
  getPresignedUrl(
    companyId: number | string,
    type: string,
    typeId: number | string,
    filename: string,
    options: { thumb?: boolean; expiresIn?: number } = {}
  ): Observable<PresignFileResult> {
    const params: string[] = [];
    if (options.thumb) { params.push('thumb=true'); }
    if (options.expiresIn) { params.push(`expiresIn=${options.expiresIn}`); }
    const queryString = params.length ? `?${params.join('&')}` : '';

    const url = `${environment.apiMicroservice}${StorageService.STORAGE_BASE}/uploads/` +
      `${companyId}/${type}/${typeId}/${filename}${queryString}`;

    return this.http.get<PresignFileResult>(url).pipe(take(1));
  }

  /**
   * Presign a batch of paths in one request.
   *
   * `POST v1/aws/storage/presign-batch`
   *   body    `{ files: [{ path, includeThumb }], expiresIn }`
   *   returns `{ success, files: [{ exists, url, thumbUrl }] }`
   *
   * The response array is index-aligned with the request and is never filtered
   * or reordered, which is what lets callers match results back by index.
   */
  presignBatch(files: PresignFileRequest[], expiresIn?: number): Observable<PresignBatchResponse> {
    const url = `${environment.apiMicroservice}${StorageService.STORAGE_BASE}/presign-batch`;

    return this.http.post<PresignBatchResponse>(url, {
      files,
      expiresIn: expiresIn || StorageService.DEFAULT_EXPIRES_IN
    }).pipe(take(1));
  }

  /**
   * Presign any number of paths, chunked to MAX_BATCH and issued sequentially so
   * we never fan out an unbounded number of HeadObject calls at once.
   *
   * Always emits an array the same length as `files`, index-aligned with it.
   * Entries are the per-file result, or null where the file could not be
   * resolved. Never errors.
   */
  presignAll(files: PresignFileRequest[], expiresIn?: number): Observable<(PresignFileResult | null)[]> {
    if (!files.length) { return of([]); }

    const chunks: PresignFileRequest[][] = [];
    for (let i = 0; i < files.length; i += StorageService.MAX_BATCH) {
      chunks.push(files.slice(i, i + StorageService.MAX_BATCH));
    }

    const requests = chunks.map((chunk) =>
      this.presignBatch(chunk, expiresIn).pipe(
        map((res) => {
          const resolved = (res && res.success && Array.isArray(res.files)) ? res.files : [];
          // Match by index, never by path.
          return chunk.map((_, i) => resolved[i] ?? null);
        }),
        catchError((error) => {
          this.warn('presignAll', error);
          return of(chunk.map(() => null));
        })
      )
    );

    // Sequential, not parallel. Each request completes thanks to take(1).
    return concat(...requests).pipe(
      toArray(),
      map((groups) => groups.reduce<(PresignFileResult | null)[]>((acc, g) => acc.concat(g), []))
    );
  }

  // =====================================================================
  // Public resolvers
  // =====================================================================

  /**
   * Design position artwork on order detail (`/orders/:id`) and the guest live
   * link (`/order/:token`) — resolves `image_1..image_4` to presigned S3 URLs
   * where S3 has the object, and leaves them untouched otherwise.
   *
   * Must run AFTER `rewriteOrderImageHosts`, which is what turns the backend's
   * value into the `/uploads/{companyId}/order_design_position/{odpId}/{file}`
   * form this keys off. (The backend already emits that shape — see
   * api-microservices `designPositionsV2` — the rewrite just normalizes the host.)
   *
   * Writes the full presigned URL back to `image_N` and the thumbnail variant to
   * a new `image_N_thumb`. Keeping both matters because the template uses the
   * full URL as the click-through target while rendering the small thumb in the
   * cell; collapsing them to the thumb would quietly downgrade "view artwork".
   * This mirrors cs-legacy's `resolvePositionImages`, which likewise kept
   * `image_N_url_photo` alongside `image_N_url_thumb`.
   *
   * Note `position.mockup` carries a duplicate of the `image_1` URL and is left
   * alone, matching `rewriteOrderImageHosts`, which does not touch it either.
   *
   * @param orders raw order records (positions mutated in place)
   * @returns the same array, after resolution has been attempted
   */
  resolvePositionImages<T = any>(orders: T[]): Observable<T[]> {
    if (!orders || !orders.length) { return of(orders); }

    try {
      const targets: ResolveTarget[] = [];

      for (const order of orders) {
        const designs = (order as any)?.designs ?? (order as any)?.design ?? [];
        if (!Array.isArray(designs)) { continue; }

        for (const design of designs) {
          const positions = design?.positions ?? design?.design_position ?? [];
          if (!Array.isArray(positions)) { continue; }

          for (const position of positions) {
            if (!position) { continue; }

            for (let i = 1; i <= 4; i++) {
              const field = `image_${i}`;
              const key = this.s3KeyFromUploadsUrl(position[field]);
              if (!key) { continue; }

              targets.push({
                key,
                apply: (result) => {
                  const full = this.fullUrlOf(result);
                  if (full) { position[field] = full; }
                  // Only a genuine thumb, never thumbUrlOf's fall-back-to-original:
                  // image_N already holds the full URL, so copying it into
                  // image_N_thumb would just be a confusing duplicate. Absent a
                  // thumb the template falls back to image_N on its own.
                  if (result.thumbUrl) { position[`${field}_thumb`] = result.thumbUrl; }
                }
              });
            }
          }
        }
      }

      return this.applyTargets(targets, orders, 'resolvePositionImages');
    } catch (error) {
      this.warn('resolvePositionImages', error);
      return of(orders);
    }
  }

  /**
   * Order list rows (orders table Proof column, and the Orders-home pipeline
   * previews) — resolves `mockup_img` to a presigned S3 URL where S3 has the
   * object, and leaves it untouched otherwise.
   *
   * Unlike cs-legacy's equivalent, this keys off `mockup_img` itself rather than
   * `design_pos[0]`: the client-platform list row carries no `odp_id` /
   * `design_mokup_image` / `company_id` fields, but the URL the backend already
   * emits contains the complete S3 key after `/uploads/`. That also means no
   * `companyId` argument is needed — the company segment comes from the path,
   * so there is no client-vs-decorator id ambiguity to get wrong.
   *
   * Rows whose `mockup_img` is null are left alone: the backend only emits a URL
   * when the underlying `image_1` is non-empty, so a null here means there is no
   * object in S3 to find either.
   *
   * @param orders raw list rows (mutated in place)
   * @returns the same array, after resolution has been attempted
   */
  resolveOrderThumbnails<T extends { mockup_img?: any }>(orders: T[]): Observable<T[]> {
    if (!orders || !orders.length) { return of(orders); }

    try {
      const targets: ResolveTarget[] = [];

      for (const row of orders) {
        const key = this.s3KeyFromUploadsUrl(row?.mockup_img);
        if (!key) { continue; }

        targets.push({
          key,
          apply: (result) => {
            // The cell renders at 2.5rem, so prefer the thumb and fall back to
            // the original when only the original made it to S3.
            const url = this.thumbUrlOf(result);
            if (url) { row.mockup_img = url; }
          }
        });
      }

      return this.applyTargets(targets, orders, 'resolveOrderThumbnails');
    } catch (error) {
      this.warn('resolveOrderThumbnails', error);
      return of(orders);
    }
  }

  // =====================================================================
  // Shared resolver machinery
  // =====================================================================

  /**
   * Presign every target's key and hand each result to its `apply` callback.
   *
   * Results are matched to targets strictly by index, and `apply` is only called
   * for objects S3 actually has — an unresolved entry leaves the backend's
   * original URL in place, which is the fail-soft contract. Emits `subject`
   * unchanged on timeout or error.
   */
  private applyTargets<S>(targets: ResolveTarget[], subject: S, method: string): Observable<S> {
    if (!targets.length) { return of(subject); }

    const files: PresignFileRequest[] = targets.map((t) => ({ path: t.key, includeThumb: true }));

    return this.presignAll(files).pipe(
      timeout(StorageService.RESOLVE_TIMEOUT_MS),
      map((results) => {
        results.forEach((result, idx) => {
          if (!result || result.exists !== true) { return; }
          targets[idx].apply(result);
        });
        return subject;
      }),
      catchError((error) => {
        this.warn(method, error);
        return of(subject);
      })
    );
  }

  // =====================================================================
  // Helpers
  // =====================================================================

  /**
   * Extract the S3 key from a legacy `/uploads/` URL, or null when the URL is
   * not a resolvable legacy upload.
   *
   * Returns null for:
   *  - empty / non-string values
   *  - URLs already served from the S3-backed image host (nothing to presign)
   *  - anything without an `/uploads/` segment
   *  - paths that don't have the expected 4 segments, since a malformed key
   *    would just cost a pointless HeadObject
   */
  private s3KeyFromUploadsUrl(value: any): string | null {
    if (typeof value !== 'string' || !value) { return null; }
    if (value.includes('img.culturestudio.app')) { return null; }

    const idx = value.indexOf(StorageService.UPLOADS_MARKER);
    if (idx === -1) { return null; }

    const key = value.slice(idx + StorageService.UPLOADS_MARKER.length).split('?')[0];
    if (!key) { return null; }

    const segments = key.split('/');
    if (segments.length !== 4 || segments.some((s) => !s)) { return null; }

    return key;
  }

  /** Best URL for a small cell: prefer the thumb, fall back to the original. */
  private thumbUrlOf(result: PresignFileResult | null): string | null {
    if (!result || result.exists !== true) { return null; }
    return result.thumbUrl || result.url || null;
  }

  /** Full-size URL, for click-through / lightbox targets. */
  private fullUrlOf(result: PresignFileResult | null): string | null {
    if (!result || result.exists !== true) { return null; }
    return result.url || null;
  }

  private warn(method: string, error: unknown): void {
    console.warn(`StorageService.${method} failed, falling back to original URLs:`, error);
  }
}
